/**
 * Parser markdown minimal, FARA HTML (B3, spec §6).
 *
 * Subsetul: titluri (`#`..`######`), liste (`- `/`* `, `1. `), paragrafe, `**bold**`/`__bold__`,
 * `*cursiv*`/`_cursiv_`, `[text](url)`. Iesirea e un arbore de noduri, nu un sir HTML: componenta
 * `Markdown` il transforma in elemente React, deci orice `<tag>` din sursa ajunge TEXT (React il
 * escapeaza). De aceea nu exista si nu trebuie sa existe `dangerouslySetInnerHTML` pe calea asta.
 *
 * Linkurile trec prin `safeHref`: doar http, https, mailto. Orice altceva (javascript:, data:,
 * relative) pierde linkul si pastreaza doar textul.
 */

export type Inline =
  | { t: 'text'; v: string }
  | { t: 'strong'; c: Inline[] }
  | { t: 'em'; c: Inline[] }
  | { t: 'link'; href: string; c: Inline[] };

export type Block =
  | { t: 'p'; c: Inline[] }
  | { t: 'h'; level: 2 | 3 | 4; c: Inline[] }
  | { t: 'ul'; items: Inline[][] }
  // `start`: numarul primului element, doar cand nu e 1 (textul autorului „2. b" ramane „2.").
  | { t: 'ol'; items: Inline[][]; start?: number };

const SCHEME_PERMISA = /^(https?:|mailto:)/i;

/** Doar http:, https:, mailto:. Altfel null (linkul se randeaza ca text simplu). */
export function safeHref(href: string): string | null {
  const h = href.trim();
  if (h !== href) return null; // spatiu la inceput/sfarsit: nu ghicim ce a vrut autorul
  return SCHEME_PERMISA.test(h) ? h : null;
}

const esteAlfanumeric = (ch: string | undefined) => ch !== undefined && /[\p{L}\p{N}]/u.test(ch);
const esteSpatiu = (ch: string | undefined) => ch === undefined || /\s/.test(ch);

/** Adauga text, lipindu-l de nodul text precedent (arborele ramane minimal si comparabil). */
function adaugaText(noduri: Inline[], v: string) {
  if (!v) return;
  const ultim = noduri[noduri.length - 1];
  if (ultim && ultim.t === 'text') ultim.v += v;
  else noduri.push({ t: 'text', v });
}

function adaugaToate(noduri: Inline[], altele: Inline[]) {
  for (const n of altele) {
    if (n.t === 'text') adaugaText(noduri, n.v);
    else noduri.push(n);
  }
}

/** `](url)` dupa eticheta: url-ul poate contine paranteze echilibrate (ex. `alert(1)`). */
function citesteLink(s: string, start: number): { eticheta: string; url: string; sfarsit: number } | null {
  const inchideEticheta = s.indexOf('](', start + 1);
  if (inchideEticheta === -1) return null;
  const eticheta = s.slice(start + 1, inchideEticheta);
  if (!eticheta || eticheta.includes('[') || eticheta.includes(']')) return null;
  let adancime = 1;
  for (let j = inchideEticheta + 2; j < s.length; j++) {
    if (s[j] === '(') adancime++;
    else if (s[j] === ')') {
      adancime--;
      if (adancime === 0) return { eticheta, url: s.slice(inchideEticheta + 2, j), sfarsit: j + 1 };
    }
  }
  return null;
}

/** Indexul delimitatorului de inchidere pentru `**`/`__`, sau -1. */
function inchidereDubla(s: string, delim: string, start: number): number {
  let k = s.indexOf(delim, start);
  while (k !== -1) {
    // Intr-un sir de trei (`***`), inchiderea dubla e ultima pereche: `**a *b***` -> strong(a, em(b)).
    if (s[k + 2] === delim[0]) k++;
    const continutValid = k > start && !esteSpatiu(s[k - 1]);
    const dupaValid = delim[0] !== '_' || !esteAlfanumeric(s[k + 2]);
    if (continutValid && dupaValid) return k;
    k = s.indexOf(delim, k + 1);
  }
  return -1;
}

/** Indexul delimitatorului simplu de inchidere pentru `*`/`_`, sau -1. */
function inchidereSimpla(s: string, ch: string, start: number): number {
  for (let k = start; k < s.length; k++) {
    if (s[k] !== ch) continue;
    if (s[k + 1] === ch) { k++; continue; } // parte dintr-un `**`/`__` interior
    if (k === start || esteSpatiu(s[k - 1])) continue;
    if (ch === '_' && esteAlfanumeric(s[k + 1])) continue; // `_` in interiorul unui cuvant
    return k;
  }
  return -1;
}

export function parseInline(src: string): Inline[] {
  const s = src ?? '';
  const noduri: Inline[] = [];
  let i = 0;

  // Fix round 1 (review Task 6): fara asta, fiecare deschidere neinchisa scana pana la capat,
  // deci timpul era patratic (162 ms pe 10.000 de caractere de `**a `, 13,7 s pe 100.000).
  // Daca o cautare de la pozitia `p` n-a gasit inchidere pentru un delimitator, nici o cautare de
  // la o pozitie mai mare n-o gaseste (conditiile de validitate doar se inaspresc), deci retinem
  // cea mai mica pozitie fara inchidere si nu mai cautam de acolo incolo.
  const faraInchidere: Record<string, number> = {};
  const cauta = (delim: string, start: number, cautare: () => number): number => {
    const limita = faraInchidere[delim];
    if (limita !== undefined && start >= limita) return -1;
    const k = cautare();
    if (k === -1) faraInchidere[delim] = limita === undefined ? start : Math.min(limita, start);
    return k;
  };
  while (i < s.length) {
    const ch = s[i];

    if (ch === '[') {
      const link = citesteLink(s, i);
      if (link) {
        const copii = parseInline(link.eticheta);
        const href = safeHref(link.url);
        if (href) noduri.push({ t: 'link', href, c: copii });
        else adaugaToate(noduri, copii);
        i = link.sfarsit;
        continue;
      }
    }

    if ((ch === '*' || ch === '_') && s[i + 1] === ch) {
      const delim = ch + ch;
      const deschidereValida = !esteSpatiu(s[i + 2]) && (ch !== '_' || !esteAlfanumeric(s[i - 1]));
      const k = deschidereValida ? cauta(delim, i + 2, () => inchidereDubla(s, delim, i + 2)) : -1;
      if (k !== -1) {
        noduri.push({ t: 'strong', c: parseInline(s.slice(i + 2, k)) });
        i = k + 2;
        continue;
      }
      adaugaText(noduri, delim);
      i += 2;
      continue;
    }

    if (ch === '*' || ch === '_') {
      const deschidereValida = !esteSpatiu(s[i + 1]) && (ch !== '_' || !esteAlfanumeric(s[i - 1]));
      const k = deschidereValida ? cauta(ch, i + 1, () => inchidereSimpla(s, ch, i + 1)) : -1;
      if (k !== -1) {
        noduri.push({ t: 'em', c: parseInline(s.slice(i + 1, k)) });
        i = k + 1;
        continue;
      }
    }

    adaugaText(noduri, ch);
    i++;
  }
  return noduri;
}

const TITLU = /^(#{1,6})\s+(.*)$/;
const ELEMENT_UL = /^\s*[-*]\s+(.*)$/;
// Fix round 1 (review Task 6): doar 1-2 cifre. „2024. Ediția a fost bună" e un an, nu pasul 2024,
// si trebuie sa ramana paragraf, cum il vedea `whitespace-pre-wrap`.
const ELEMENT_OL = /^\s*(\d{1,2})[.)]\s+(.*)$/;

/**
 * Entitatile HTML din text (editorul de lectii, MDXEditor, salveaza spatiul de la final de rand ca
 * `&#x20;`, iar textele lipite pot aduce `&nbsp;`/`&amp;`) devin caracterul real. Se aplica DOAR pe
 * nodurile text, DUPA parsare: `&#42;x&#42;` ramane „*x*" literal, nu devine cursiv. Href-urile nu
 * trec pe aici (sunt deja validate de `safeHref`). Iesirea ramane text escapat de React, nu HTML.
 * O entitate invalida (in afara Unicode, surogat, caracter de control) ramane text literal.
 */
const ENTITATI_NUMITE: Record<string, string> = {
  nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'",
};
const ENTITATE = /&(?:#(\d{1,7})|#[xX]([0-9a-fA-F]{1,6})|([a-zA-Z]{2,6}));/g;

export function decodeazaEntitati(s: string): string {
  if (!s.includes('&')) return s;
  return s.replace(ENTITATE, (intreg, zecimal?: string, hexa?: string, nume?: string) => {
    if (nume !== undefined) return ENTITATI_NUMITE[nume] ?? intreg;
    const cod = zecimal !== undefined ? Number(zecimal) : parseInt(hexa!, 16);
    const valid =
      cod <= 0x10ffff &&
      !(cod >= 0xd800 && cod <= 0xdfff) &&
      !(cod < 0x20 && cod !== 0x09 && cod !== 0x0a) &&
      !(cod >= 0x7f && cod <= 0x9f);
    return valid ? String.fromCodePoint(cod) : intreg;
  });
}

function decodeazaInline(noduri: Inline[]): Inline[] {
  return noduri.map((n) =>
    n.t === 'text' ? { t: 'text', v: decodeazaEntitati(n.v) } : { ...n, c: decodeazaInline(n.c) }
  );
}

function decodeazaBloc(b: Block): Block {
  if (b.t === 'ul' || b.t === 'ol') return { ...b, items: b.items.map(decodeazaInline) };
  return { ...b, c: decodeazaInline(b.c) };
}

function nivelTitlu(diezi: number): 2 | 3 | 4 {
  if (diezi <= 2) return 2; // pagina are deja h1 = titlul activitatii
  if (diezi === 3) return 3;
  return 4;
}

export function parseMarkdown(src: string): Block[] {
  const linii = (src ?? '').replace(/\r\n?/g, '\n').split('\n');
  const blocuri: Block[] = [];
  let i = 0;

  while (i < linii.length) {
    const linie = linii[i];
    if (!linie.trim()) { i++; continue; }

    const titlu = TITLU.exec(linie);
    if (titlu) {
      blocuri.push({ t: 'h', level: nivelTitlu(titlu[1].length), c: parseInline(titlu[2].trim()) });
      i++;
      continue;
    }

    if (ELEMENT_UL.test(linie)) {
      const items: Inline[][] = [];
      let m: RegExpExecArray | null;
      while (i < linii.length && (m = ELEMENT_UL.exec(linii[i]))) {
        items.push(parseInline(m[1].trim()));
        i++;
      }
      blocuri.push({ t: 'ul', items });
      continue;
    }

    const primulOl = ELEMENT_OL.exec(linie);
    if (primulOl) {
      // Numarul primului element devine `start` (daca nu e 1): „2. b" dupa un paragraf ramane „2.".
      const start = Number(primulOl[1]);
      const items: Inline[][] = [];
      let m: RegExpExecArray | null;
      while (i < linii.length && (m = ELEMENT_OL.exec(linii[i]))) {
        items.push(parseInline(m[2].trim()));
        i++;
      }
      blocuri.push(start === 1 ? { t: 'ol', items } : { t: 'ol', start, items });
      continue;
    }

    // Paragraf: linii consecutive pana la o linie goala, un titlu sau o lista. Newline-ul simplu
    // ramane in text (componenta il randeaza ca <br />), ca textele vechi, scrise pentru
    // `whitespace-pre-wrap`, sa arate la fel.
    const bucati: string[] = [];
    while (
      i < linii.length && linii[i].trim() &&
      !TITLU.test(linii[i]) && !ELEMENT_UL.test(linii[i]) && !ELEMENT_OL.test(linii[i])
    ) {
      bucati.push(linii[i].trim());
      i++;
    }
    blocuri.push({ t: 'p', c: parseInline(bucati.join('\n')) });
  }
  return blocuri.map(decodeazaBloc);
}
